/**
 * Client-side image compressor used by the loan-car customer submission
 * forms (kiosk QR + customer-phone QR + booking-link token form).
 *
 * Why: phone cameras produce 3-6 MB JPEGs at 4032×3024 etc. Sending them
 * as base64 (~33% bloat → 5-8 MB JSON body) over 4G/3G takes 30-60 s and
 * blocks the customer on the "Submit" button. We don't need anywhere near
 * that resolution for a driver's-licence record — a long-edge of 1280 px
 * at JPEG quality 0.72 yields a sharp, legible image at ~150-300 KB.
 *
 * Returns a JPEG data-URL (always; even if input was PNG/HEIC).
 * Falls back to raw FileReader if anything goes wrong, so the user never
 * gets stuck because of a corner-case codec.
 */
export async function compressImageFile(
  file,
  { maxDim = 1280, quality = 0.72 } = {},
) {
  if (!file) return null;

  // Skip if already small (<400 KB) — no point burning CPU to shave bytes.
  if (file.size <= 400 * 1024) {
    return readAsDataUrl(file);
  }

  try {
    const dataUrl = await readAsDataUrl(file);
    const img = await loadImage(dataUrl);
    let { width, height } = img;
    const longest = Math.max(width, height);
    if (longest > maxDim) {
      const scale = maxDim / longest;
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    // White background so PNG transparency doesn't go black in JPEG.
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', quality);
  } catch (err) {
    // Any decode/canvas failure (HEIC on older browsers, tainted source,
    // etc.) → fall back to raw bytes. Slower upload but at least works.
     
    console.warn('Image compression failed, falling back to raw:', err);
    return readAsDataUrl(file);
  }
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
